import React from 'react';
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { OpenClassSessionStatus } from '../types/OpenClassSession';

type OpenClassSessionStatusBadgeProps = {
  status: OpenClassSessionStatus;
  pill?: boolean;
};

const dotStyles: Record<
  OpenClassSessionStatus,
  { textStyle: string; dotStyle: string }
> = {
  confirmed: { textStyle: 'text-success', dotStyle: 'confirmed-dot' },
  preliminary: { textStyle: 'text-warningYellow', dotStyle: 'preliminary-dot' },
  ordered: { textStyle: 'text-blueGrayMediumDark', dotStyle: 'potential-dot' },
  canceled: { textStyle: 'text-red-800', dotStyle: 'cancelled-dot' },
};

const pillStyles: Record<
  OpenClassSessionStatus,
  { style: string; icon: React.ReactNode }
> = {
  confirmed: {
    style: 'bg-successLight text-success',
    icon: <CheckOutlined className="text-success pr-1" />,
  },
  preliminary: {
    style: 'bg-yellow-100 text-yellow-800',
    icon: <ClockCircleOutlined className="text-yellow-800 pr-1" />,
  },
  ordered: { style: 'bg-blueGrayLight text-blueGrayMediumDark', icon: null },
  canceled: {
    style: 'bg-red-100 text-red-800',
    icon: <CloseOutlined className="text-red-800 pr-1" />,
  },
};

const OpenClassSessionStatusBadge: React.FC<
  OpenClassSessionStatusBadgeProps
> = ({ status, pill }) => {
  const { t } = useTranslation();
  const label = t(`components.OpenClassSessionStatusBadge.${status}`);

  if (pill) {
    return (
      <div
        className={`h-6 px-2 inline-flex text-xs font-semibold rounded-full ${pillStyles[status].style}`}>
        <div className="flex items-center">
          {pillStyles[status].icon}
          {label}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center">
      <div
        className={`w-3 h-3 mr-1 rounded-default ${dotStyles[status].dotStyle}`}
      />
      <div
        className={`font-semibold items-center ${dotStyles[status].textStyle}`}>
        {label}
      </div>
    </div>
  );
};

export default OpenClassSessionStatusBadge;
